import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Calendar, ArrowRight, X } from "lucide-react";
import { Reveal } from "@/components/Section";
import { HeroBanner } from "@/components/Section";
import { formatNewsDate } from "@/data/news";
import { useContent } from "@/context/ContentContext";

export default function News() {
  const { news: NEWS, content } = useContent();
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const id = searchParams.get("post");
    if (id) {
      const item = NEWS.find((n) => n.id === id);
      if (item) setSelected(item);
    }
  }, [searchParams, NEWS]);

  const close = () => {
    setSelected(null);
    if (searchParams.get("post")) {
      searchParams.delete("post");
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div data-testid="news-page">
      <section className="relative overflow-hidden border-b border-border carbon-texture">
        <HeroBanner src={content.page_banners?.news} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-secondary">
            {content.page_headers?.news_eyebrow || "Latest Updates"}
          </span>
          <h1 className="mt-4 font-heading text-5xl font-bold tracking-tight text-white md:text-6xl">
            {content.page_headers?.news_title || "News & Events"}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-white/70">
            {content.page_headers?.news_description || "Announcements, partnerships, celebrations and milestones from Runner Trade Park Limited."}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {NEWS.map((n, i) => (
            <Reveal key={n.id} delay={(i % 3) * 0.06}>
              <button
                data-testid={`news-card-${n.id}`}
                onClick={() => setSelected(n)}
                className="group flex h-full w-full flex-col overflow-hidden border border-border bg-card text-left transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-xl"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={n.image}
                    alt={n.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-0 top-0 bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                    {n.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5 text-primary" />
                    {formatNewsDate(n.date)}
                  </span>
                  <h3 className="mt-2 font-heading text-lg font-semibold leading-tight tracking-tight text-foreground">
                    {n.title}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                    {n.excerpt}
                  </p>
                  <span className="mt-auto flex items-center gap-1 pt-4 text-xs font-bold uppercase tracking-wide text-primary">
                    Read More <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-h-[90vh] max-w-2xl gap-0 overflow-y-auto rounded-xl border-2 border-border p-0 [&>button]:hidden"
          data-testid="news-detail-dialog"
        >
          {selected && (
            <div>
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                <img src={selected.image} alt={selected.title} className="h-full w-full object-cover" />
                <span className="absolute left-0 top-0 bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                  {selected.tag}
                </span>
                <button
                  type="button"
                  onClick={close}
                  data-testid="news-detail-close"
                  aria-label="Close"
                  className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-primary"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 md:p-8">
                <DialogHeader>
                  <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5 text-primary" />
                    {formatNewsDate(selected.date)}
                  </span>
                  <DialogTitle className="mt-2 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground">
                    {selected.title}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    News article: {selected.title}
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground">
                  {selected.body.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
